// Plantillas de correo editables (editor de React Email). Solo definicion:
// funciones en queries.ts / mutations.ts / compile.ts.
//
//   email_templates          por empresa y destino; una puede ser la por
//                            defecto. `doc` es el JSON del editor (fuente de
//                            verdad); `compiled` es el HTML/texto con tokens
//                            `{{@N}}` que arma `compile.ts` tras cada cambio
//   email_template_versions  historial inmutable para ver cambios y restaurar
//
// Destinos (`scope` + `target`):
//   notification   tipo de notificacion ("invoice_completed", ...)
//   system_table   tabla del sistema: correo que acompana al PDF ("invoices")
//   collection     id de una coleccion propia
//   script         clave libre que usa un script (`notify.sendEmail({ template })`)
//
// Permisos: modulo `custom_fields`, igual que las plantillas PDF.

import { v, type Infer } from "convex/values";
import { defineTableWithInternalControl, internalControl } from "../../lib/shared/validators";

export const emailTemplateScope = v.union(
  v.literal("notification"),
  v.literal("system_table"),
  v.literal("collection"),
  v.literal("script"),
);
export type EmailTemplateScope = Infer<typeof emailTemplateScope>;

export const validatorCompiledEmail = v.object({
  html: v.string(),
  text: v.string(),
  /** Claves de variables; `{{@N}}` apunta a `variables[N]`. */
  variables: v.array(v.string()),
  /** `rev` de la plantilla que se compilo. */
  rev: v.number(),
});

export const validatorEmailTemplate = v.object({
  scope: emailTemplateScope,
  target: v.string(),
  collectionId: v.optional(v.id("collections")),
  name: v.string(),
  /** Admite `{{clave}}`. */
  subject: v.string(),
  /** Texto de vista previa en la bandeja de entrada. */
  preheader: v.optional(v.string()),
  /** JSON del editor (TipTap) como texto (`parseEmailDocArg`); filas viejas: objeto. */
  doc: v.any(),
  /** Sube con cada cambio de `doc`/`preheader`; la compilacion lo compara. */
  rev: v.number(),
  compiled: v.optional(validatorCompiledEmail),
  /** Error de la ultima compilacion (se borra al compilar bien). */
  compileError: v.optional(v.string()),
  isDefault: v.boolean(),
  enabled: v.boolean(),
  /** Numero de la ultima version en `email_template_versions`. */
  version: v.number(),
  internalControl,
});
export type EmailTemplateDoc = Infer<typeof validatorEmailTemplate>;

export const email_templates = defineTableWithInternalControl(validatorEmailTemplate)
  .index("by_company_and_scope_and_target", ["internalControl.companyId", "scope", "target"])
  .index("by_company_and_scope_and_target_and_default", [
    "internalControl.companyId",
    "scope",
    "target",
    "isDefault",
  ]);

export const validatorEmailTemplateVersion = v.object({
  templateId: v.id("email_templates"),
  version: v.number(),
  name: v.string(),
  subject: v.string(),
  preheader: v.optional(v.string()),
  doc: v.any(),
  /** "create" | "update" | "restore". */
  reason: v.string(),
  internalControl,
});

export const email_template_versions = defineTableWithInternalControl(validatorEmailTemplateVersion).index(
  "by_template_and_version",
  ["templateId", "version"],
);
